import { friendlyDbError } from '../../../utils/friendlyDbError';
import type { StateCreator } from 'zustand';
import type { MessagesSlice, WhatsAppChatStore, Message, MessageStatus } from '../types';
import { supabase } from '../../../supabase';
import { getWahaService } from '../../../services/wahaApiService';
import { WhatsAppDbService } from '../../../services/whatsappDbService';
import { debugLog } from '../../../../components/whatsapp2/chat/ChatDebugPanel';
import { countsAsNewUnread } from '../../../services/whatsapp/wahaReadState';
import {
  applyMessageToChats,
  isDuplicateMessage,
  deduplicateDbMessages,
  normalizeChatId,
} from '../helpers';
import { applyTranscriptions, fetchGroupTranscriptions } from '../groupTranscriptions';

export const createMessagesSlice: StateCreator<
  WhatsAppChatStore,
  [],
  [],
  MessagesSlice
> = (set, get) => ({
  messages: [],
  messagesLoading: false,
  messagesError: null,
  loadOlderError: null,
  hasOlderMessages: true,
  oldestMessageTimestamp: null,
  wahaOffset: 0,
  messagesSource: 'waha',
  dbOldestTimestamp: null,

  loadMessages: async (chatId: string) => {
    const { wahaConfig } = get();
    if (!wahaConfig) return;
    const initialMessages = new Set(get().messages);
    const isCurrentRequest = () => get().wahaConfig === wahaConfig && get().selectedChatId === chatId;
    // Preserve messages received/sent while DB or provider requests were in flight.
    // Existing history is still reconciled against the provider as before.
    const preserveConcurrentMessages = (loaded: Message[]) => {
      const byId = new Map(loaded.map(message => [message.id, message]));
      for (const message of get().messages) {
        if (!initialMessages.has(message)) byId.set(message.id, message);
      }
      return [...byId.values()].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    };

    // ── Branch CLOUD: messaggi SOLO dal DB (whatsapp_messages via webhook), no WAHA API ──
    const cloudProvider = get().provider;
    if (cloudProvider && cloudProvider.type !== 'whatsapp') {
      set({ messagesLoading: true, messagesError: null });
      try {
        const e164 = chatId.replace(/\D/g, ''); // da "<e164>@c.us" a E.164 puro
        const cloudMsgs = await WhatsAppDbService.loadCloudMessagesFromDb(
          wahaConfig.configId, e164,
          { limit: 50, organizationId: wahaConfig.organizationId },
        );
        if (!isCurrentRequest()) return; // stale
        const ascending = [...cloudMsgs].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        set({
          messages: preserveConcurrentMessages(ascending),
          messagesLoading: false,
          messagesSource: 'db',
          hasOlderMessages: cloudMsgs.length >= 50,
          oldestMessageTimestamp: ascending.length ? ascending[0].timestamp.getTime() : null,
        });
      } catch (error) {
        if (!isCurrentRequest()) return;
        console.error('[Store] loadMessages (cloud) error:', error);
        set({
          messagesError: friendlyDbError(error) || 'Errore caricamento messaggi',
          messagesLoading: false,
        });
      }
      return;
    }

    set({ messagesLoading: true, messagesError: null });

    // Helper per arricchire messaggi con nomi dal DB (per gruppi)
    const enrichMessagesWithNames = async (messages: Message[], isGroup: boolean): Promise<Message[]> => {
      if (!isGroup || !wahaConfig.organizationId) return messages;

      // 1. Self-enrichment: se un messaggio ha il nome del sender, applicalo a tutti
      //    i messaggi dello stesso sender che non ce l'hanno
      const knownNames = new Map<string, string>();
      for (const m of messages) {
        if (m.senderJid && m.senderName && !knownNames.has(m.senderJid)) {
          knownNames.set(m.senderJid, m.senderName);
        }
      }

      let enriched = knownNames.size > 0
        ? messages.map(m => {
            if (m.senderJid && !m.senderName && knownNames.has(m.senderJid)) {
              return { ...m, senderName: knownNames.get(m.senderJid) };
            }
            return m;
          })
        : messages;

      // 2. CRM lookup: per i sender ancora senza nome, cerca nei contatti CRM
      const senderJids = [...new Set(
        enriched
          .filter((m) => m.direction === 'inbound' && m.senderJid && !m.senderName)
          .map((m) => m.senderJid!)
      )];

      if (senderJids.length === 0) return enriched;

      try {
        const namesMap = await WhatsAppDbService.lookupContactNames(senderJids, wahaConfig.organizationId);
        if (namesMap.size === 0) return enriched;

        return enriched.map((m) => {
          if (m.senderJid && !m.senderName && namesMap.has(m.senderJid)) {
            return { ...m, senderName: namesMap.get(m.senderJid) };
          }
          return m;
        });
      } catch (error) {
        console.error('[Store] Error looking up sender names:', error);
        return enriched;
      }
    };

    try {
      const isGroup = chatId.endsWith('@g.us');
      debugLog('Store', `📥 loadMessages: chatId=${chatId.slice(0,20)}... isGroup=${isGroup}`);

      // STEP 1: Carica da DB (istantaneo, ~50ms) — mostra subito per UX reattiva
      const dbMessagesRaw = await WhatsAppDbService.loadMessagesFromDb(
        wahaConfig.configId,
        chatId,
        { limit: 30, organizationId: wahaConfig.organizationId }
      );
      const dbMessages = deduplicateDbMessages(dbMessagesRaw);

      if (isGroup && dbMessages.length > 0) {
        const inbound = dbMessages.filter(m => m.direction === 'inbound');
        const withSender = inbound.filter(m => m.senderJid);
        const withName = inbound.filter(m => m.senderName);
        debugLog('Store', `STEP 1 (DB): ${dbMessages.length} msg, ${inbound.length} inbound, ${withSender.length} con senderJid, ${withName.length} con senderName`,
          inbound.slice(0, 5).map(m => ({ id: m.id?.slice(0,20), senderJid: m.senderJid || '❌ MANCANTE', senderName: m.senderName || '❌ MANCANTE' })), 'data');
      }

      if (dbMessages.length > 0 && isCurrentRequest()) {
        for (const message of dbMessages) initialMessages.add(message);
        set({ messages: preserveConcurrentMessages(dbMessages), messagesSource: 'db' });
      }

      // Stale check: se l'utente ha già cambiato chat, non proseguire
      if (!isCurrentRequest()) return;

      // STEP 2: WAHA = fonte di verità per messaggi recenti (~500ms)
      const service = getWahaService();
      const wahaMessages = await service.getChatMessages(chatId, {
        limit: 30,
        offset: 0,
      });

      if (isGroup && wahaMessages.length > 0) {
        const inbound = wahaMessages.filter(m => m.direction === 'inbound');
        const withSender = inbound.filter(m => m.senderJid);
        const withName = inbound.filter(m => m.senderName);
        debugLog('Store', `STEP 2 (WAHA API): ${wahaMessages.length} msg, ${inbound.length} inbound, ${withSender.length} con senderJid, ${withName.length} con senderName`,
          inbound.slice(0, 5).map(m => ({ id: m.id?.slice(0,20), senderJid: m.senderJid || '❌ MANCANTE', senderName: m.senderName || '❌ MANCANTE' })), 'data');
      }

      // STEP 3: Arricchisci WAHA con dati DB-only (reactions, transcriptions)
      const dbById = new Map(dbMessages.map(m => [m.id, m]));
      const enrichedWaha = wahaMessages.map(m => {
        const dbMatch = dbById.get(m.id);
        if (dbMatch) {
          return {
            ...m,
            reactions: dbMatch.reactions,
            transcription: dbMatch.transcription,
            senderName: m.senderName || dbMatch.senderName,
          };
        }
        return m;
      });

      // STEP 4: DB messages SOLO se più vecchi della finestra WAHA
      // Questo elimina duplicati e messaggi fantasma nella finestra recente
      const wahaIdSet = new Set(wahaMessages.map(m => m.id));
      const oldestWahaTs = enrichedWaha.length > 0
        ? Math.min(...enrichedWaha.map(m => m.timestamp.getTime()))
        : null;

      const dbOlderOnly = oldestWahaTs
        ? dbMessages.filter(m =>
            m.timestamp.getTime() < oldestWahaTs && !wahaIdSet.has(m.id)
          )
        : [];

      // STEP 5: Merge finale = DB vecchi + WAHA recenti (con dedup)
      const merged = [...dbOlderOnly, ...enrichedWaha].sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
      );
      // Dedup finale: rimuovi duplicati per ID o per content/timestamp con IDs diversi
      // Tolleranza adattiva: 30s per messaggi con testo, 15s per media senza caption
      let allMessages = merged.filter((msg, index) => {
        return !merged.slice(0, index).some(prev => {
          if (prev.id === msg.id) return true;
          const timeDiffMs = Math.abs(prev.timestamp.getTime() - msg.timestamp.getTime());
          if (prev.direction !== msg.direction) return false;
          if (!msg.content && !prev.content) {
            return timeDiffMs <= 15000 && prev.type === msg.type;
          }
          return timeDiffMs <= 30000 && prev.content === msg.content;
        });
      });

      // STEP 6: Per gruppi, arricchisci con nomi da clients/client_contacts
      if (isGroup) {
        const beforeEnrich = allMessages.filter(m => m.direction === 'inbound' && !m.senderName).length;
        allMessages = await enrichMessagesWithNames(allMessages, true);
        const afterEnrich = allMessages.filter(m => m.direction === 'inbound' && !m.senderName).length;
        debugLog('Store', `STEP 6 (Enrich): ${beforeEnrich} senza nome prima → ${afterEnrich} senza nome dopo`,
          allMessages.filter(m => m.direction === 'inbound').slice(0, 5).map(m => ({ senderJid: m.senderJid || '❌', senderName: m.senderName || '❌' })), 'data');
      }

      const oldestTimestamp = allMessages.length > 0
        ? Math.min(...allMessages.map((m) => m.timestamp.getTime()))
        : null;

      // Stale check: se l'utente ha già cambiato chat, non sovrascrivere
      if (!isCurrentRequest()) return;

      set({
        messages: preserveConcurrentMessages(allMessages),
        messagesLoading: false,
        hasOlderMessages: wahaMessages.length >= 30,
        oldestMessageTimestamp: oldestTimestamp,
        wahaOffset: wahaMessages.length,
        messagesSource: 'hybrid',
      });

      // STEP 7: gruppi, testo dei vocali salvati sul numero di un collega (non blocca la vista)
      if (isGroup) {
        const texts = await fetchGroupTranscriptions(allMessages, wahaConfig.configId, chatId);
        if (isCurrentRequest() && Object.keys(texts).length > 0) {
          set({ messages: applyTranscriptions(get().messages, texts) });
        }
      }
    } catch (error) {
      if (!isCurrentRequest()) return; // Stale
      console.error('[Store] loadMessages error:', error);
      set({
        messagesError: friendlyDbError(error) || 'Errore caricamento messaggi',
        messagesLoading: false,
      });
    }
  },

  loadOlderMessages: async () => {
    const {
      wahaConfig,
      selectedChatId,
      hasOlderMessages,
      messages,
      messagesLoading,
      wahaOffset,
    } = get();

    // Evita chiamate multiple mentre già sta caricando
    if (!wahaConfig || !selectedChatId || !hasOlderMessages || messagesLoading) {
      return;
    }

    // Setta loading state e resetta errore
    set({ messagesLoading: true, loadOlderError: null });

    // ── Branch CLOUD: messaggi più vecchi dal DB per chat_identifier_e164 ──
    const cloudProvider = get().provider;
    if (cloudProvider && cloudProvider.type !== 'whatsapp') {
      try {
        const e164 = selectedChatId.replace(/\D/g, '');
        const oldest = get().oldestMessageTimestamp ?? undefined;
        const older = await WhatsAppDbService.loadCloudMessagesFromDb(
          wahaConfig.configId, e164,
          { limit: 50, beforeTimestamp: oldest, organizationId: wahaConfig.organizationId },
        );
        if (older.length === 0) {
          set({ hasOlderMessages: false, messagesLoading: false });
          return;
        }
        const merged = [...older, ...get().messages].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        const seen = new Set<string>();
        const deduped = merged.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)));
        set({
          messages: deduped,
          oldestMessageTimestamp: deduped.length ? deduped[0].timestamp.getTime() : (oldest ?? null),
          hasOlderMessages: older.length >= 50,
          messagesLoading: false,
        });
      } catch (error) {
        console.error('[Store] loadOlderMessages (cloud) error:', error);
        set({ messagesLoading: false, loadOlderError: 'Impossibile caricare messaggi precedenti' });
      }
      return;
    }

    try {
      const isGroup = selectedChatId.endsWith('@g.us');

      // Usa wahaOffset per paginazione WAHA (i messaggi sono ordinati DESC, offset salta i più recenti)
      const service = getWahaService();

      const olderMessages = await service.getChatMessages(selectedChatId, {
        limit: 30,
        offset: wahaOffset,
      });

      if (olderMessages.length === 0) {
        set({ hasOlderMessages: false, messagesLoading: false });
        return;
      }

      // Deduplica con logica robusta (ID + fallback su timestamp+direction+content)
      let newMessages = olderMessages.filter((m) => !isDuplicateMessage(messages, m));

      // Se TUTTI i messaggi sono duplicati, significa che WAHA sta tornando sempre gli stessi
      // (offset non funziona correttamente) - ferma la paginazione
      if (newMessages.length === 0) {
        set({ hasOlderMessages: false, messagesLoading: false });
        return;
      }

      // Per gruppi, arricchisci con nomi da clients/client_contacts
      if (isGroup && wahaConfig.organizationId) {
        const senderJids = [...new Set(
          newMessages
            .filter((m) => m.direction === 'inbound' && m.senderJid && !m.senderName)
            .map((m) => m.senderJid!)
        )];

        if (senderJids.length > 0) {
          try {
            const namesMap = await WhatsAppDbService.lookupContactNames(senderJids, wahaConfig.organizationId);
            if (namesMap.size > 0) {
              newMessages = newMessages.map((m) => {
                if (m.senderJid && namesMap.has(m.senderJid)) {
                  return { ...m, senderName: namesMap.get(m.senderJid) };
                }
                return m;
              });
            }
          } catch (error) {
            console.error('[Store] Error looking up sender names:', error);
          }
        }
      }

      // Aggiorna wahaOffset per prossima chiamata
      const newWahaOffset = wahaOffset + olderMessages.length;

      // Calcola nuovo oldest timestamp
      const newOldest = newMessages.length > 0
        ? Math.min(...newMessages.map((m) => m.timestamp.getTime()))
        : null;

      // Ordina tutti i messaggi per timestamp (più vecchi prima)
      const allMessages = [...messages, ...newMessages].sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
      );

      set({
        messages: allMessages,
        wahaOffset: newWahaOffset,
        oldestMessageTimestamp: newOldest || get().oldestMessageTimestamp,
        hasOlderMessages: olderMessages.length >= 30,
        messagesLoading: false,
      });

      // Gruppi: testo dei vocali delle pagine vecchie, anche se salvati sul numero di un collega
      if (isGroup) {
        const texts = await fetchGroupTranscriptions(newMessages, wahaConfig.configId, selectedChatId);
        if (get().selectedChatId === selectedChatId && Object.keys(texts).length > 0) {
          set({ messages: applyTranscriptions(get().messages, texts) });
        }
      }
    } catch (error) {
      console.error('[Store] loadOlderMessages error:', error);
      set({
        messagesLoading: false,
        loadOlderError: 'Impossibile caricare messaggi precedenti',
      });
    }
  },

  addMessage: (message: Message) => {
    const { messages, selectedChatId, pendingMessages, lidToCusMap } = get();

    // Risolvi @lid → @c.us se necessario (WAHA NOWEB usa linked device IDs)
    let resolvedChatId = message.chatId;
    if (resolvedChatId.endsWith('@lid')) {
      const mapped = lidToCusMap.get(resolvedChatId);
      if (mapped) {
        resolvedChatId = mapped;
      }
    }

    // Risolvi ANCHE selectedChatId @lid → @c.us per confronto simmetrico
    // (WAHA 2026.3.x invia chatId come @lid, ma transformDbPayload può aver già risolto il msg a @c.us)
    let resolvedSelectedChatId = selectedChatId || '';
    if (resolvedSelectedChatId.endsWith('@lid')) {
      const mapped = lidToCusMap.get(resolvedSelectedChatId);
      if (mapped) {
        resolvedSelectedChatId = mapped;
      }
    }

    // Normalizza chatId per confronto (rimuovi @c.us/@lid per evitare mismatch)
    const normalizedMessageChatId = normalizeChatId(resolvedChatId);
    const normalizedSelectedChatId = normalizeChatId(resolvedSelectedChatId);

    // Group sync/service events belong in the open timeline, never in recent
    // activity or unread counters. WAHA can replay hundreds after an outbound send.
    if (message.type === 'system') {
      if (normalizedMessageChatId === normalizedSelectedChatId && !messages.some(m => m.id === message.id)) {
        set({ messages: [...messages, message].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()) });
      }
      return;
    }

    // Se il messaggio non è della chat corrente → aggiorna sidebar, non chat view
    if (normalizedMessageChatId !== normalizedSelectedChatId) {
      console.log('[WA] addMessage: other chat →', message.chatId?.slice(0, 20), message.direction);

      // Incrementa unreadCount + aggiorna lastMessage + riordina sidebar
      const { chats } = get();
      // Solo messaggi davvero nuovi: un vecchio messaggio recuperato in ritardo o una
      // chat gia' letta dal telefono non fanno salire il contatore (wahaReadState.ts).
      const targetChat = chats.find((c) => normalizeChatId(c.id) === normalizedMessageChatId);
      const countIt = message.direction === 'inbound' && countsAsNewUnread(targetChat, message.timestamp.getTime());
      const chatsWithUnread = countIt
        ? chats.map((c) =>
            normalizeChatId(c.id) === normalizedMessageChatId
              ? { ...c, unreadCount: (c.unreadCount || 0) + 1 }
              : c
          )
        : chats;
      set({ chats: applyMessageToChats(chatsWithUnread, message) });
      if (countIt) {
        const matchChat = chats.find(c => normalizeChatId(c.id) === normalizedMessageChatId);
        debugLog('Webhook', `unread++ per "${matchChat?.name || resolvedChatId.slice(0, 20)}" → ${(matchChat?.unreadCount || 0) + 1}`);
      }
      return;
    }

    // Check duplicati (ID esatto + fallback contenuto con tolleranza adattiva)
    const isDuplicate = messages.some((m) => m.id === message.id) ||
      messages.some((m) => {
        const timeDiffMs = Math.abs(m.timestamp.getTime() - message.timestamp.getTime());
        const dirMatch = m.direction === message.direction;
        if (!dirMatch) return false;
        // Per media senza testo: tolleranza 15s + match su type
        if (!message.content && !m.content) {
          return timeDiffMs < 15000 && m.type === message.type;
        }
        // Per messaggi con testo: tolleranza 30s + match su content
        return timeDiffMs < 30000 && m.content === message.content;
      });
    if (isDuplicate) return;

    // Verifica se è un messaggio pending che sta arrivando da realtime
    // Tolerance aumentata a 60s per gestire ritardi del server e webhook con DB-first
    const pendingIndex = pendingMessages.findIndex(
      (p) =>
        normalizeChatId(p.chatId) === normalizedMessageChatId &&
        Math.abs(p.timestamp.getTime() - message.timestamp.getTime()) < 60000 &&
        p.content === message.content
    );

    if (pendingIndex >= 0) {
      // Match trovato - rimuovi pending e aggiungi messaggio reale
      const pending = pendingMessages[pendingIndex];
      const newPending = [...pendingMessages];
      newPending.splice(pendingIndex, 1);

      // Preserva blob URL se il messaggio reale non ha mediaUrl (per audio/media)
      const finalMessage = {
        ...message,
        mediaUrl: message.mediaUrl || pending.mediaPreviewUrl,
      };

      console.log('[WA] addMessage: pending replaced →', message.id);

      set({
        messages: [...messages, finalMessage],
        pendingMessages: newPending,
        chats: applyMessageToChats(get().chats, finalMessage),
      });
    } else if (message.direction === 'outbound') {
      // Messaggio outbound senza match timestamp - cerca per solo contenuto
      // (fallback per quando il server è lento)
      const fallbackIndex = pendingMessages.findIndex(
        (p) => normalizeChatId(p.chatId) === normalizedMessageChatId && p.content === message.content
      );

      if (fallbackIndex >= 0) {
        const pending = pendingMessages[fallbackIndex];
        const newPending = [...pendingMessages];
        newPending.splice(fallbackIndex, 1);

        const finalMessage = {
          ...message,
          mediaUrl: message.mediaUrl || pending.mediaPreviewUrl,
        };

        console.log('[WA] addMessage: fallback content match →', message.id);

        set({
          messages: [...messages, finalMessage],
          pendingMessages: newPending,
          chats: applyMessageToChats(get().chats, finalMessage),
        });
      } else {
        // Nessun pending trovato - messaggio inviato dal telefono (non dal CRM)
        // Con DB-first, il messaggio è già nel DB (arrivato via webhook)

        console.log('[WA] addMessage: outbound (from phone) →', message.id);

        set({
          messages: [...messages, message],
          chats: applyMessageToChats(get().chats, message),
        });

        // Se l'utente ha inviato dal telefono, la chat è implicitamente letta
        get().sendReadReceipt(normalizedMessageChatId);
      }
    } else {
      // Messaggio inbound o altri casi
      // Con DB-first, tutti i messaggi dal DB sono già decriptati

      console.log('[WA] addMessage: inbound →', message.id, message.content?.slice(0, 30));

      set({
        messages: [...messages, message],
        chats: applyMessageToChats(get().chats, message),
      });
    }
  },

  updateMessageStatus: (messageId: string, status: MessageStatus, extras?: Pick<Message, 'metadata' | 'failureReason' | 'transcription'>) => {
    const { messages } = get();
    const updated = messages.map((m) =>
      m.id === messageId ? { ...m, status, ...(extras || {}) } : m
    );
    set({ messages: updated });
  },

  updateMessageEdited: (messageId: string, newContent: string) => {
    const { messages } = get();
    const updated = messages.map((m) =>
      m.id === messageId ? { ...m, content: newContent, isEdited: true } : m
    );
    set({ messages: updated });
  },

  updateMessageDeleted: (messageId: string) => {
    const { messages } = get();
    const updated = messages.map((m) =>
      m.id === messageId ? { ...m, isDeleted: true } : m
    );
    set({ messages: updated });
  },

  updateMessageReaction: async (messageId: string, action: 'added' | 'removed', broadcastChatId?: string) => {
    const { wahaConfig, messages, selectedChatId } = get();
    debugLog('Reactions', `updateMessageReaction called: ${action}`, { messageId, broadcastChatId, selectedChatId, messagesCount: messages.length });
    if (!wahaConfig) {
      debugLog('Reactions', '⚠️ wahaConfig is null, aborting', {}, 'warn');
      return;
    }

    // Extract short ID from serialized format (e.g., "false_393xxx@c.us_ABC123" -> "ABC123")
    const extractShortId = (id: string): string => {
      const parts = id.split('_');
      if (parts.length >= 3 && (parts[0] === 'true' || parts[0] === 'false')) {
        return parts[parts.length - 1];
      }
      return id;
    };

    const reactionShortId = extractShortId(messageId);

    // Find message that matches (by exact ID or by short ID suffix)
    const matchingMessage = messages.find((m) => {
      if (m.id === messageId) return true;
      return extractShortId(m.id) === reactionShortId;
    });

    if (!matchingMessage) {
      debugLog('Reactions', `⚠️ Message not found in local state, shortId: ${reactionShortId}`, { messageId, messagesInState: messages.slice(0, 5).map(m => m.id) }, 'warn');
      const reactionChatId = broadcastChatId;
      if (reactionChatId && selectedChatId && !selectedChatId.includes(reactionChatId.replace('@c.us', '').replace('@g.us', ''))) {
        debugLog('Reactions', 'Reaction for different chat, skipping', { reactionChatId, selectedChatId });
        return;
      }

      // Ricarica i messaggi dal DB (include le reactions aggiornate)
      debugLog('Reactions', 'Reloading messages from DB to get reactions...');
      if (selectedChatId) {
        const dbMessages = await WhatsAppDbService.loadMessagesFromDb(
          wahaConfig.configId,
          selectedChatId,
          { limit: 50, organizationId: wahaConfig.organizationId }
        );
        if (dbMessages.length > 0) {
          set({ messages: dbMessages });
        }
      }
      return;
    }

    const actualMessageId = matchingMessage.id;
    debugLog('Reactions', `✅ Message found: ${actualMessageId}`, { broadcastId: messageId });

    try {
      const { data: reactions, error } = await supabase
        .from('whatsapp_message_reactions')
        .select('reaction, sender_jid')
        .in('message_id', [messageId, actualMessageId])
        .eq('config_id', wahaConfig.configId);

      if (error) {
        console.error('[Store] Error querying reactions:', error);
        return;
      }

      debugLog('Reactions', `DB query returned ${reactions?.length || 0} reactions`, { reactions });

      if (reactions && reactions.length > 0) {
        const reactionMap = new Map<string, { emoji: string; count: number; senderJids: string[] }>();
        for (const r of reactions) {
          const existing = reactionMap.get(r.reaction);
          if (existing) {
            existing.count++;
            existing.senderJids.push(r.sender_jid);
          } else {
            reactionMap.set(r.reaction, { emoji: r.reaction, count: 1, senderJids: [r.sender_jid] });
          }
        }

        const aggregatedReactions = Array.from(reactionMap.values());
        const updated = get().messages.map((m) =>
          m.id === actualMessageId ? { ...m, reactions: aggregatedReactions } : m
        );
        set({ messages: updated });
      } else {
        const updated = get().messages.map((m) =>
          m.id === actualMessageId ? { ...m, reactions: [] } : m
        );
        set({ messages: updated });
      }
    } catch (error) {
      console.error('[Store] Failed to reload reactions:', error);
    }
  },
});
